import React, { useState } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Legend, Tooltip } from 'recharts';
import {
  Users,
  MessageSquare,
  Brain,
  FileText,
  CheckCircle,
  AlertCircle,
  XCircle,
  HelpCircle,
  Loader2,
  ExternalLink,
  Clock,
  RefreshCw,
  Cpu,
  MemoryStick,
  HardDrive,
  Network,
} from 'lucide-react';
import { useAgentUserStats, useDatabaseStats, useServiceStatus, useSystemMetrics } from '../hooks';
import type { ServiceStatus } from '../api';
import type { SourceFilter } from '../api/agentUsers';

const formatServerTime = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const getStatusIcon = (status: string) => {
  const iconClass = 'w-5 h-5';

  switch (status.toLowerCase()) {
    case 'operational':
    case 'healthy':
      return <CheckCircle className={`${iconClass} text-green-500`} />;
    case 'degraded':
      return <AlertCircle className={`${iconClass} text-yellow-500`} />;
    case 'offline':
    case 'error':
      return <XCircle className={`${iconClass} text-red-500`} />;
    default:
      return <HelpCircle className={`${iconClass} text-slate-400`} />;
  }
};

const getStatusBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case 'operational':
    case 'healthy':
      return 'bg-green-100 text-green-700';
    case 'degraded':
      return 'bg-yellow-100 text-yellow-700';
    case 'offline':
    case 'error':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

const ALERT_THRESHOLD = 90;

const getMetricColor = (percent: number): string => {
  if (percent >= ALERT_THRESHOLD) return 'text-red-600';
  return 'text-slate-800';
};

const getBarColor = (percent: number): string => {
  if (percent >= ALERT_THRESHOLD) return 'bg-red-500';
  if (percent >= 70) return 'bg-amber-500';
  return 'bg-blue-500';
};

const FILTER_OPTIONS: { value: SourceFilter | 'all'; label: string }[] = [
  { value: 'real', label: 'Real' },
  { value: 'playground', label: 'PG' },
  { value: 'all', label: 'All' },
];

const Dashboard: React.FC = () => {
  const [sourceFilter, setSourceFilter] = useState<SourceFilter | 'all'>('real');

  // Convert 'all' → undefined for API
  const apiSource: SourceFilter = sourceFilter === 'all' ? undefined : sourceFilter;

  const { data: userStats, isLoading: usersLoading } = useAgentUserStats(apiSource);
  const { data: dbStats, isLoading: dbLoading } = useDatabaseStats();
  const { data: serviceStatusData, isLoading: servicesLoading } = useServiceStatus();
  const {
    data: metrics,
    isLoading: metricsLoading,
    refetch: refetchMetrics,
    isFetching: metricsFetching,
  } = useSystemMetrics();
  const serverTime = serviceStatusData?.server_time;

  const isLoading = usersLoading || dbLoading;

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Stats cards data — memories/documents/messages come from the filtered stats API
  const stats = [
    {
      title: 'Total Users',
      value: userStats?.total_users?.toLocaleString() || '0',
      icon: Users,
      color: 'blue',
    },
    {
      title: 'Total Messages',
      value: userStats?.total_messages?.toLocaleString() || '0',
      icon: MessageSquare,
      color: 'purple',
    },
    {
      title: 'Total Memories',
      value: userStats?.total_memories?.toLocaleString() || '0',
      icon: Brain,
      color: 'green',
    },
    {
      title: 'Total Documents',
      value: userStats?.total_documents?.toLocaleString() || '0',
      icon: FileText,
      color: 'amber',
    },
  ];

  // Tier distribution for pie chart
  const tierData = userStats?.users_by_tier
    ? Object.entries(userStats.users_by_tier).map(([tier, count]) => ({
        name: tier,
        value: count as number,
      }))
    : [];

  // Default services
  const defaultServices: ServiceStatus[] = [
    {
      name: 'PostgreSQL DB',
      status: dbStats ? 'Operational' : 'Unknown',
      latency: `${dbStats?.connections?.active_connections || 0} active`,
    },
    { name: 'Agent Server', status: 'Unknown', latency: 'N/A' },
  ];

  const services = serviceStatusData?.services || defaultServices;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-slate-500">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold text-slate-800">Dashboard Overview</h2>
          {/* Source filter */}
          <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg">
            {FILTER_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setSourceFilter(value)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  sourceFilter === value ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">Last updated: Just now</span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const bgColors: Record<string, string> = {
            blue: 'bg-blue-50 text-blue-600',
            purple: 'bg-purple-50 text-purple-600',
            green: 'bg-green-50 text-green-600',
            amber: 'bg-amber-50 text-amber-600',
          };
          return (
            <div
              key={index}
              className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2 rounded-lg ${bgColors[stat.color]}`}>
                  <Icon size={20} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-slate-800">{stat.value}</h3>
              <p className="text-sm text-slate-500">{stat.title}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tier Distribution */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-800 mb-6">Users by Tier</h3>
          <div className="h-64">
            {tierData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tierData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value, percent }) => `${name} ${value}명 (${(percent * 100).toFixed(0)}%)`}
                  >
                    {tierData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                <div className="text-center">
                  <AlertCircle size={32} className="mx-auto mb-2" />
                  <p>No user data available</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* System Metrics */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-800">Metrics</h3>
            <button
              onClick={() => refetchMetrics()}
              disabled={metricsFetching}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={metricsFetching ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {metrics ? (
            <div className="space-y-4">
              {/* Resource Gauges */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'CPU', value: metrics.cpu.usage_percent, icon: Cpu, detail: null },
                  { label: 'RAM', value: metrics.memory.ram.percent, icon: MemoryStick, detail: null },
                  {
                    label: 'Disk',
                    value: metrics.disk.usage.percent,
                    icon: HardDrive,
                    detail: `${formatBytes(metrics.disk.usage.used_bytes)} / ${formatBytes(metrics.disk.usage.total_bytes)}`,
                  },
                  {
                    label: 'Swap',
                    value: metrics.memory.swap.percent,
                    icon: MemoryStick,
                    detail: `${formatBytes(metrics.memory.swap.used_bytes)} / ${formatBytes(metrics.memory.swap.total_bytes)}`,
                  },
                ].map(({ label, value, icon: Icon, detail }) => (
                  <div key={label} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <Icon size={14} className="text-slate-400" />
                        <span className="text-xs font-medium text-slate-600">{label}</span>
                      </div>
                      <span className={`text-sm font-bold ${getMetricColor(value)}`}>{value.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${getBarColor(value)}`}
                        style={{ width: `${Math.min(value, 100)}%` }}
                      />
                    </div>
                    {detail && <div className="text-[10px] text-slate-400 mt-1 text-right">{detail}</div>}
                  </div>
                ))}
              </div>

              {/* Load Average */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-600">Load Average</span>
                  <span className="text-xs text-slate-500">{metrics.cpu.cores.logical} cores</span>
                </div>
                <div className="flex gap-4 mt-1">
                  {(['1min', '5min', '15min'] as const).map((key) => (
                    <div key={key} className="text-center">
                      <span className="text-sm font-bold text-slate-800">{metrics.cpu.load_average[key]}</span>
                      <span className="text-xs text-slate-400 ml-1">{key}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Memory Detail */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <span className="text-xs font-medium text-slate-600">Memory</span>
                <div className="flex justify-between mt-1 text-xs text-slate-500">
                  <span>Used: {formatBytes(metrics.memory.ram.used_bytes)}</span>
                  <span>Available: {formatBytes(metrics.memory.ram.available_bytes)}</span>
                  <span>Total: {formatBytes(metrics.memory.ram.total_bytes)}</span>
                </div>
              </div>

              {/* Network I/O */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-1.5 mb-1">
                  <Network size={14} className="text-slate-400" />
                  <span className="text-xs font-medium text-slate-600">Network I/O (cumulative)</span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Sent: {formatBytes(metrics.network.bytes_sent)}</span>
                  <span>Recv: {formatBytes(metrics.network.bytes_recv)}</span>
                </div>
                {(metrics.network.errin > 0 || metrics.network.errout > 0) && (
                  <div className="mt-1 text-xs text-red-500">
                    Errors: in={metrics.network.errin} out={metrics.network.errout}
                  </div>
                )}
              </div>

              {/* Uptime */}
              <div className="text-xs text-slate-400 text-right">
                {metrics.hostname} · uptime {formatUptime(metrics.uptime_seconds)}
              </div>
            </div>
          ) : metricsLoading ? (
            <div className="flex items-center justify-center h-64 text-slate-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-400">
              <div className="text-center">
                <Cpu size={32} className="mx-auto mb-2" />
                <p>Failed to load metrics</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Service Status & LangFuse Link */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Service Status</h3>
            {servicesLoading && <Loader2 size={16} className="animate-spin text-slate-400" />}
          </div>
          {serverTime?.current_time && (
            <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-blue-50 rounded-lg text-sm text-blue-700">
              <Clock size={16} />
              <span className="font-medium">Server Time:</span>
              <span className="font-mono">{formatServerTime(serverTime.current_time)} KST</span>
            </div>
          )}
          <div className="space-y-3">
            {services.map((service, i) => (
              <div key={i} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(service.status)}
                    <span className="font-medium text-slate-700">{service.name}</span>
                    {service.version && <span className="text-xs font-mono text-slate-400">v{service.version}</span>}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-mono text-slate-400">{service.latency}</span>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(service.status)}`}
                    >
                      {service.status}
                    </span>
                  </div>
                </div>
                {(service.model || service.dimension || service.base_url) && (
                  <div className="mt-1.5 ml-8 text-xs text-slate-500">
                    {service.model && <span className="mr-3">Model: {service.model}</span>}
                    {service.dimension && <span className="mr-3">Dim: {service.dimension}</span>}
                    {service.base_url && <span className="font-mono">{service.base_url}</span>}
                  </div>
                )}
                {service.api_keys && Object.keys(service.api_keys).length > 0 && (
                  <div className="mt-1.5 ml-8 flex flex-wrap gap-2">
                    {Object.entries(service.api_keys).map(([key, info]) => {
                      const colorCls =
                        info.status === 'ok'
                          ? 'bg-green-50 text-green-600'
                          : info.status === 'not_configured'
                          ? 'bg-slate-100 text-slate-500'
                          : 'bg-red-50 text-red-600';
                      const dotCls =
                        info.status === 'ok'
                          ? 'bg-green-500'
                          : info.status === 'not_configured'
                          ? 'bg-slate-400'
                          : 'bg-red-500';
                      const label =
                        info.status === 'not_configured'
                          ? `${key} · not set`
                          : info.status === 'invalid_key'
                          ? `${key} · invalid`
                          : key;
                      return (
                        <span
                          key={key}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono ${colorCls}`}
                          title={`status: ${info.status}, configured: ${info.configured}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
                          {label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* LangFuse Quick Access */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">LLM Monitoring</h3>
          <p className="text-slate-500 text-sm mb-6">
            LLM 호출 추적, 비용 분석, 프롬프트 관리는 LangFuse에서 직접 확인하세요.
          </p>
          <a
            href={`${window.location.protocol}//${window.location.hostname}:3000`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <div>
              <p className="font-medium text-slate-700">LangFuse Dashboard</p>
              <p className="text-xs text-slate-500">Traces, 프롬프트 관리, 비용 분석</p>
            </div>
            <ExternalLink size={18} className="text-slate-400" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
